"use client";

import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Check, ExternalLink } from "lucide-react";
import PageContent from "../../page-content";
import Link from "next/link";
import { exampleConfigs } from "./config";
import { exampleResults } from "./results";

interface BoundingBox {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}

interface Example {
  id: string;
  title: string;
  description: string;
  image: string;
  prompt: string;
  response: string;
  boxes: BoundingBox[];
  responseTimeMs: number;
  inputTokens: number;
  outputTokens: number;
  playgroundLink: string;
}

// Combine configs with results (keyed by ID)
// Images are in public/ and can be .jpeg or .png
const examples: Example[] = exampleConfigs.map((config) => {
  const result = exampleResults[config.id];
  
  // Use extension from result if available, otherwise default to .jpeg
  const imageExt = result.imageExt || 'jpeg';
  const imagePath = `/images/solutions/robot-vision/${config.imageFile}.${imageExt}`;
  
  // Parse bounding boxes from response
  let boxes: BoundingBox[] = [];
  try {
    boxes = JSON.parse(result.response);
  } catch (e) {
    console.error('Failed to parse bounding boxes:', e);
  }
  
  return {
    id: config.id,
    title: config.title,
    description: config.description,
    prompt: config.prompt,
    image: imagePath,
    response: result.response,
    boxes: boxes,
    responseTimeMs: result.responseTimeMs,
    inputTokens: result.inputTokens,
    outputTokens: result.outputTokens,
    playgroundLink: `/c/playground?image=${encodeURIComponent(imagePath)}&prompt=${encodeURIComponent(config.prompt)}&skill=detect`,
  };
});

const Examples = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, exampleTitle: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(exampleTitle);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const calculateCost = (inputTokens: number, outputTokens: number): string => {
    const inputCost = (inputTokens / 1_000_000) * 0.30;
    const outputCost = (outputTokens / 1_000_000) * 2.50;
    const totalCost = inputCost + outputCost;
    
    return `$${totalCost.toFixed(6)}`;
  };

  // Component to render image with canvas overlay for bounding boxes
  const ImageWithBoundingBoxes = ({ image, boxes, onDimensionsChange }: { 
    image: string; 
    boxes: BoundingBox[];
    onDimensionsChange?: (height: number) => void;
  }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [scaledDimensions, setScaledDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;

      let isMounted = true;
      const imgElement = new window.Image();

      imgElement.onload = () => {
        if (!isMounted || !ctx || !canvas) return;
        
        const MAX_DIMENSION = 512;
        let scaledWidth = imgElement.width;
        let scaledHeight = imgElement.height;

        if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
          if (scaledWidth >= scaledHeight) {
            const aspectRatio = scaledHeight / scaledWidth;
            scaledWidth = MAX_DIMENSION;
            scaledHeight = MAX_DIMENSION * aspectRatio;
          } else {
            const aspectRatio = scaledWidth / scaledHeight;
            scaledHeight = MAX_DIMENSION;
            scaledWidth = MAX_DIMENSION * aspectRatio;
          }
        }

        canvas.width = scaledWidth * 2;
        canvas.height = scaledHeight * 2;
        canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.scale(2, 2);

        if (scaledDimensions.width !== scaledWidth || scaledDimensions.height !== scaledHeight) {
          setScaledDimensions({ width: scaledWidth, height: scaledHeight });
          if (onDimensionsChange) {
            onDimensionsChange(scaledHeight);
          }
        }

        ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

        // Draw bounding boxes
        if (boxes.length > 0) {
          const boxColor = "rgb(34, 197, 94)"; // green-500 (neon green)
          const transparentColor = "rgba(34, 197, 94, 0.2)";

          boxes.forEach((box) => {
            const x = box.x_min * scaledWidth;
            const y = box.y_min * scaledHeight;
            const width = (box.x_max - box.x_min) * scaledWidth;
            const height = (box.y_max - box.y_min) * scaledHeight;

            // Fill rectangle with transparent color
            ctx.fillStyle = transparentColor;
            ctx.fillRect(x, y, width, height);

            // Draw border
            ctx.strokeStyle = boxColor;
            ctx.lineWidth = 3;
            ctx.strokeRect(x, y, width, height);
          });
        }
      };

      imgElement.src = image;

      return () => {
        isMounted = false;
      };
    }, [image, boxes, scaledDimensions.width, scaledDimensions.height, onDimensionsChange]);

    return (
      <canvas
        ref={canvasRef}
        className="rounded-lg border w-full"
      />
    );
  };

  const generateCodeExamples = (prompt: string) => {
    const escapedPrompt = prompt.replace(/"/g, '\\"');
    
    return {
      python: `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load robot camera image
image = Image.open("path/to/robot_camera.jpg")

# Detect objects in the scene
result = model.detect(image, "${escapedPrompt}")
objects = result["objects"]
request_id = result["request_id"]
print(f"Found {len(objects)} objects")
print(f"Request ID: {request_id}")`,
      
      nodejs: `import { vl } from 'moondream';
import fs from 'fs';

// Initialize with API key
const model = new vl({ apiKey: "your-api-key" });

// Load robot camera image
const image = fs.readFileSync("path/to/robot_camera.jpg");

// Detect objects in the scene
const result = await model.detect({
  image: image,
  object: "${escapedPrompt}"
});
console.log(\`Found \${result.objects.length} objects\`);
console.log(\`Request ID: \${result.request_id}\`);
console.log(result.objects);`,
      
      curl: `curl -X POST https://api.moondream.ai/v1/detect \\
  -H 'Content-Type: application/json' \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -d '{
    "image_url": "data:image/jpeg;base64,...",
    "object": "${escapedPrompt}"
  }'`
    };
  };

  const ExampleCard = ({ example }: { example: Example }) => {
    const [canvasHeight, setCanvasHeight] = useState(0);

    return (
      <Card className="overflow-hidden">
        <CardHeader className="bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="text-2xl">{example.title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{example.description}</p>
            </div>
            <Button asChild size="sm" className="ml-4">
              <Link href={example.playgroundLink}>
                Try in playground
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Image + Prompt/Response Layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Image with Bounding Boxes - Left Side */}
            <div className="lg:w-1/2 shrink-0 flex">
              <ImageWithBoundingBoxes 
                image={example.image}
                boxes={example.boxes}
                onDimensionsChange={setCanvasHeight}
              />
            </div>

            {/* Object and Response - Right Side */}
            <div 
              className="lg:w-1/2 flex flex-col gap-4"
              style={{ height: canvasHeight > 0 ? `${canvasHeight}px` : undefined }}
            >
              <div className="shrink-0">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-2">
                  Detection target
                </h3>
                <Card className="bg-muted/50">
                  <CardContent className="p-4">
                    <p className="text-sm">{example.prompt}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Detection output
                  </h3>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {example.responseTimeMs}ms • {example.inputTokens + example.outputTokens} tokens • {calculateCost(example.inputTokens, example.outputTokens)}
                  </div>
                </div>
                <Card className="bg-muted/50 flex-1 min-h-0 overflow-hidden">
                  <CardContent className="p-4 h-full overflow-y-auto">
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {example.response}
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

              {/* Code Example */}
              <div className="mt-6 space-y-2">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Code Example
                </h3>
                <Tabs defaultValue="python" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="python">Python</TabsTrigger>
                    <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                    <TabsTrigger value="curl">cURL</TabsTrigger>
                  </TabsList>
                  
                  {(['python', 'nodejs', 'curl'] as const).map((lang) => {
                    const codeExamples = generateCodeExamples(example.prompt);
                    const code = codeExamples[lang];
                    const copyKey = `${example.id}-${lang}`;
                    
                    return (
                      <TabsContent key={lang} value={lang} className="mt-4">
                        <div className="relative">
                          <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm border">
                            <code className={`language-${lang === 'nodejs' ? 'javascript' : lang === 'curl' ? 'bash' : lang}`}>
                              {code}
                            </code>
                          </pre>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-8 w-8 p-0"
                            onClick={() => copyToClipboard(code, copyKey)}
                          >
                            {copiedCode === copyKey ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <PageContent>
      <div id="examples" className="space-y-12">
        {examples.map((example, index) => (
          <ExampleCard key={index} example={example} />
        ))}
      </div>
    </PageContent>
  );
};

export default Examples;

